"use client";

import { useQuestCompletions } from "@/components/QuestsV2/useQuestCompletions";
import { appConfig } from "@/constants";
import { usePassportProvider } from "@/context";
import {
  getDiscoverFeed,
  getQuestFromFieldData,
  getQuestSections,
} from "@/domain/gems-game/data-access/lib/cms";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { useLinkedAccountsState } from "@hooks/useLinkedAccountsState";
import { getQuests } from "@imx-tokens-ts/gems-game/data-access";
import { useMemo } from "react";
import useSWR, { type SWRConfiguration } from "swr";
import useSWRImmutable from "swr/immutable";

export const QUESTS_DATA_KEY = "/quests";
export const QUESTS_SECTIONS_DATA_KEY = "/quest_sections";
export const DISCOVER_FEED_DATA_KEY = "/discover_feed";

export const useQuests = () => {
  const {
    data: rawData,
    error,
    isLoading,
  } = useSWRImmutable(QUESTS_DATA_KEY, async () => {
    return await getQuests(fetch, appConfig.QUESTS_CMS_URL);
  });

  const processedData = useMemo(() => {
    if (!rawData) return undefined;

    return {
      featuredQuests: rawData.featuredQuests.map(getQuestFromFieldData),
      mainQuests: rawData.mainQuests.map(getQuestFromFieldData),
      sideQuests: rawData.sideQuests.map(getQuestFromFieldData),
      onboardingQuests: rawData.onboardingQuests.map(getQuestFromFieldData),
      seriesQuests: rawData.seriesQuests.map(getQuestFromFieldData),
      inGameQuests: rawData.inGameQuests.map(getQuestFromFieldData),
    };
  }, [rawData]);

  return {
    data: processedData,
    error,
    isLoading,
  };
};

export const useQuestSections = () =>
  useSWRImmutable(QUESTS_SECTIONS_DATA_KEY, () =>
    getQuestSections(fetch, appConfig.QUEST_SECTIONS_CMS_URL),
  );

type DiscoverFeedOptions = {
  shouldShowGetRewardsReadyCard?: boolean;
  getRewardsReadyInsertionIndex?: number;
  swrConfig?: SWRConfiguration;
};

export const useDiscoverFeed = ({
  shouldShowGetRewardsReadyCard = false,
  getRewardsReadyInsertionIndex = -1,
  swrConfig,
}: DiscoverFeedOptions = {}) => {
  const { rewardsOnboardingCard: rewardsOnboardingCardFFActive } =
    useFeatureFlags();

  const { completedQuests, isLoading } = useQuestCompletions(undefined, false);
  const { isInitializing, isLoggingIn } = usePassportProvider();

  const completedQuestIds = Object.keys(completedQuests ?? {});
  const { discordLinked, xLinked } = useLinkedAccountsState();

  const feedResponse = useSWR(
    isLoading || isInitializing || isLoggingIn
      ? null
      : [DISCOVER_FEED_DATA_KEY, completedQuestIds],
    () =>
      getDiscoverFeed({
        fetch,
        collectionItemsUrl: appConfig.DISCOVER_FEED_CMS_URL,
        completedQuestIds,
        discordLinked,
        xLinked,
        shouldShowGetRewardsReadyCard,
        getRewardsReadyInsertionIndex,
        rewardsOnboardingCardFFActive,
      }),
    {
      ...swrConfig,
      keepPreviousData: true,
    },
  );

  return {
    ...feedResponse,
    isLoading: feedResponse.isLoading || isLoading,
  };
};
